import { useAtom } from "jotai";

import Button from "components/Button";
import Flex from "components/Flex";
import { CameraIcon } from "components/Icons/Camera";
import PageLayout from "components/PageLayout";
import PhotoGrid from "components/PhotoGrid";
import PhotoGridDocuments from "components/PhotoGridDocuments";
import PhotoUpload from "components/PhotoUpload";
import { Photo } from "components/PhotoUpload/types";
import Text from "components/Text";

import { authoritiesPhotosAtom } from "atoms/authoritiesPhotos";
import { useCheckpoint } from "hooks/useCheckpoint";
import { useClientTranslation } from "hooks/useClientTranslation";
import { useDigitalJourneyPageRouting } from "hooks/useDigitalJourneyPageRouting";
import { useEventTracking } from "hooks/useEventTracking";
import { Checkpoint } from "types/Checkpoint";
import { processImageForUpload } from "utils/imageProcessing/processImageForUpload";

export const DJPhotosAuthoritiesPages = () => {
  const { t } = useClientTranslation();
  const { trackEvent } = useEventTracking();
  const { onNext, continueCTA } = useDigitalJourneyPageRouting();
  const targetReached = useCheckpoint(Checkpoint.CLAIM_SUBMITTED);
  const [authoritiesPhotos, setAuthoritiesPhotos] = useAtom(authoritiesPhotosAtom);

  const canContinue = true;

  const onContinue = () => {
    trackEvent({
      name: "Digital Journey Authorities Document Photos - continue",
      properties: { numberOfImages: authoritiesPhotos.length },
    });
    onNext();
  };

  const onDrop = (files: File[]) => {
    files.forEach(async (file, index) => {
      const image = await processImageForUpload({
        file,
        alt: `Authorities Document photo to upload number ${index + 1}`,
        type: Photo.Additional,
      });

      setAuthoritiesPhotos((photos) => [...photos, image]);
    });
  };

  return (
    <PageLayout
      contentSpacing="space-between"
      dataTestId="authoritiesPhotos"
      forceContinue={targetReached}
      bottomButtons={
        <Flex gap={16}>
          <PhotoUpload onDrop={onDrop} disableGalleryUploadOverride={false}>
            <Button typeVariant={canContinue ? "secondary" : "primary"} leftIcon={<CameraIcon />}>
              {t("global.add_photos")}
            </Button>
          </PhotoUpload>
          {canContinue && <Button onClick={onContinue}>{continueCTA}</Button>}
        </Flex>
      }
    >
      <Flex mx={24} mb={32} my={8} gap={16}>
        <Flex gap={8}>
          <Text as="h1" sizeVariant="xl" weight={600}>
            Scatta una foto della documentazione rilasciata dalle Autorità intervenute
          </Text>
          <Text>Ci è utile per completare la denuncia del sinistro.</Text>
        </Flex>
        {Boolean(authoritiesPhotos.length) ? (
          <PhotoGrid images={authoritiesPhotos} deleteFromAtom={setAuthoritiesPhotos} />
        ) : (
          <Flex
            align="center"
            sx={{
              "& > div": {
                width: "unset",
              },
            }}
          >
            <PhotoGridDocuments
              sxImageContainer={{
                "&&": {
                  maxHeight: "354px",
                },
              }}
              images={[
                {
                  id: "djAuthoritiesDocumentPhoto",
                  alt: "Authorities Document Photos",
                  src: "/assets/png/digitalJourney/authorities-doc-example.png",
                },
              ]}
            />
          </Flex>
        )}
      </Flex>
    </PageLayout>
  );
};
